import {
  RowDataReducerState,
  RowDataReducerAction,
  Rule,
  ProcessDependencyRulesParams,
  RowDataObject,
} from "../../../../types";
import {
  isNewRecord,
  disableAndClearField,
  setFieldDisabled,
  updateAndMaybeResetField,
  getApplicableRule,
  dispatchUpdateSchemaPropertyField,
  getCoRelationChildKeys,
  getEnableableCoRelationChildKeys,
  emptyCoRelationArray,
} from "../../helpers";

export const DEFAULT_DEPENDENCY_RULE: Rule = {
  matches: [],
};

export function processDependenyRules({
  dependencyRules,
  propertyKey,
  inputValue,
  propertySubKey,
  rowIndex,
  selectedTabSectionKey,
  rowDataReducerState,
  dispatch,
}: ProcessDependencyRulesParams): void {
  const applicableRule = getApplicableRule(
    dependencyRules,
    inputValue,
    rowIndex,
    propertySubKey,
    rowDataReducerState,
  );

  if (applicableRule === DEFAULT_DEPENDENCY_RULE) return;

  handleRuleExecution(
    applicableRule,
    propertyKey,
    rowDataReducerState,
    rowIndex,
    selectedTabSectionKey,
    dispatch,
  );
}

const handleRuleExecution = (
  rule: Rule,
  propertyKey: string,
  rowDataReducerState: RowDataReducerState,
  rowIndex: number | undefined,
  selectedTabSectionKey: string | undefined,
  dispatch: React.Dispatch<RowDataReducerAction>,
): void => {
  const { rowData } = rowDataReducerState;
  const isNew = isNewRecord(rowData);

  // fieldsToUpdate
  rule.fieldsToUpdate?.forEach(
    ({ targetKey, enumValues, allowedValues, isForExistingRecord }) => {
      if (isForExistingRecord) {
        if (!isNew) {
          dispatchUpdateSchemaPropertyField(
            dispatch,
            "enum",
            propertyKey,
            rowIndex,
            targetKey,
            enumValues,
          );
        }
        return; // Prevent further processing if Rule field has `isForExistingRecord`
      }

      updateAndMaybeResetField({
        dispatch,
        propertyKey,
        rowIndex,
        selectedTabSectionKey,
        targetKey,
        enumValues,
        allowedValues,
        rowData,
      });
    },
  );

  // fieldsToEnable
  rule.fieldsToEnable?.forEach((targetKey) =>
    setFieldDisabled(dispatch, propertyKey, rowIndex, targetKey, false),
  );

  // fieldsToDisable
  rule.fieldsToDisable?.forEach((targetKey) =>
    setFieldDisabled(dispatch, propertyKey, rowIndex, targetKey, true),
  );

  // fieldsToDisableAndClear
  rule.fieldsToDisableAndClear?.forEach((targetKey) =>
    disableAndClearField(
      dispatch,
      propertyKey,
      rowIndex,
      selectedTabSectionKey,
      targetKey,
      rowDataReducerState,
    ),
  );

  // coRelationParentsToEnable
  // - un-disables the parent schema property → re-enables add button
  // - only enables child keys that are not permanently disabled in the
  //   original schema (resetState.schema) — autopopulated fields stay disabled
  rule.coRelationParentsToEnable?.forEach((parentKey) => {
    dispatchUpdateSchemaPropertyField(
      dispatch,
      "disabled",
      parentKey,
      undefined,
      parentKey,
      false,
    );

    const enableableChildKeys = getEnableableCoRelationChildKeys(
      parentKey,
      rowDataReducerState,
    );
    const rows = rowData[parentKey] as RowDataObject[] | undefined;
    rows?.forEach((_, rowIdx) => {
      enableableChildKeys.forEach((fieldKey) => {
        setFieldDisabled(dispatch, parentKey, rowIdx, fieldKey, false);
      });
    });
  });

  // coRelationParentsToDisableAndClear
  // - disables+clears ALL child fields across all existing rows
  // - does not touch the add button or remove rows
  rule.coRelationParentsToDisableAndClear?.forEach((parentKey) => {
    const childKeys = getCoRelationChildKeys(parentKey, rowDataReducerState);
    const rows = rowData[parentKey] as RowDataObject[] | undefined;
    rows?.forEach((_, rowIdx) => {
      childKeys.forEach((fieldKey) => {
        disableAndClearField(
          dispatch,
          parentKey,
          rowIdx,
          selectedTabSectionKey,
          fieldKey,
          rowDataReducerState,
        );
      });
    });
  });

  // coRelationParentsToEmptyArray
  // - disables parent schema property → isAddButtonDisabled = true
  // - empties rowData[parentKey] = [] → RenderCoRelation useEffect fires setRows([])
  rule.coRelationParentsToEmptyArray?.forEach((parentKey) => {
    emptyCoRelationArray(dispatch, parentKey, selectedTabSectionKey); // pass selectedTabSectionKey
  });
};
